// 261023 E3-P3c1 — hàm THUẦN của chuỗi "Truyện mới" (nút Bắt đầu → ba chặng): mô hình dải, nhãn bước, lý do dừng, form D6, dấu chip chặng.
// KHÔNG import antd / react (jest của react-scripts không load được antd v6). `.tsx` chỉ vẽ và gọi API theo `primary.id`.
import type { AppendRun, WorkbenchHandoffBatchView, WorkbenchRunningJob, WorkbenchStageKey, WorkbenchView } from '../../../types/adaptationWorkbench';
import type { PushStrip } from './handoffBatchView';
import { RECHECK_LABEL } from './recheckView';
import type { RecheckStrip } from './recheckView';
import { AUTO_REPAIR_STOP_LABEL } from './autoRepairView';
import type { AutoRepairStrip } from './autoRepairView';
import type { NewStoryRunStartBody } from '../../../services/api/adaptation';
import { formatJobStepProgress } from './jobStepProgress';
import { friendlyJobNote } from './jobErrorText';
import { interruptedStripView } from './appendRunView';
import type { StageMarkView, WorkbenchTarget } from './workbenchView';
import { canApprove, dedupeViolations, withoutEpisodeClaimCodes } from '../adaptationUtils';
import { approveBlock, approveView } from './overview/overviewView';
import type { OutlineRecord } from '../../../types/adaptation';
import { nsSkeletonGateLines } from './skeletonGateView';
import { gateWaitView, GATE_BLOCKED_LINE, GATE_CONTINUE_LABEL, GATE_REGRADE_LABEL } from './chainGateView';
import { volumeGateBarLines, VOLUME_GATE_ACCEPT_LABEL, VOLUME_GATE_LABEL, VOLUME_GATE_OPEN_SOURCE_LABEL, VOLUME_GATE_RESUME_LABEL } from './volumeGateView';
import { budgetOf, CONTINUE_LABEL, interruptedCallsLine, stop1View, waitBudgetView } from './newStoryBudgetView';

export type NsView = Partial<Pick<WorkbenchView, 'appendRun' | 'interrupted' | 'newStoryRun' | 'canStartNewStory' | 'episodes' | 'budget' | 'stop1Estimate' | 'waitBudgetStepEstimate' | 'continuation' | 'skeletonGate' | 'readerLine' | 'machineSkipped'>> & { outlineFlags?: { episodesListed: number; rawChapters?: number } };

// ─── Nhận diện ──────────────────────────────────────────────────────────────

export const isNewStoryRun = (run: Pick<AppendRun, 'kind'> | null | undefined): boolean => run?.kind === 'NEW_STORY';
/** Chuỗi truyện mới đang/đã chạy (trước SAVE: chỉ có `newStoryRun`; sau SAVE: `appendRun.kind NEW_STORY`). */
export const isNewStoryView = (v: NsView | null | undefined): boolean => !!v && (isNewStoryRun(v.appendRun) || (!v.appendRun && !!v.newStoryRun));

// ─── Bước / chặng ───────────────────────────────────────────────────────────

export type NsItemKey = 'SCAN' | 'SKELETON' | 'OVERVIEW' | 'LOCALIZE' | 'SPLIT' | 'PLAN' | 'WRITE' | 'HANDOFF';
export type NsStageKey = 'c1' | 'c2' | 'c3';
export type NsItemState = 'done' | 'run' | 'wait' | 'stopped' | 'error' | 'todo';
export type NsStageState = 'done' | 'run' | 'wait' | 'stopped' | 'error' | 'todo';

const ITEMS: Array<{ key: NsItemKey; label: string; stage: NsStageKey; wb: WorkbenchStageKey }> = [
  { key: 'SCAN', label: 'Quét', stage: 'c1', wb: 'source' },
  { key: 'SKELETON', label: 'Khung xương', stage: 'c1', wb: 'skeleton' },
  { key: 'OVERVIEW', label: 'Tổng quan', stage: 'c1', wb: 'skeleton' },
  { key: 'LOCALIZE', label: 'Bản địa hoá', stage: 'c2', wb: 'localize' },
  { key: 'SPLIT', label: 'Chia tập', stage: 'c2', wb: 'split' },
  { key: 'PLAN', label: 'Chốt dàn tập', stage: 'c2', wb: 'split' },
  { key: 'WRITE', label: 'Viết', stage: 'c2', wb: 'script' },
  { key: 'HANDOFF', label: 'Đẩy sang Chapters', stage: 'c3', wb: 'script' },
];
const STAGE_TITLE: Record<NsStageKey, string> = { c1: 'Chặng 1', c2: 'Chặng 2', c3: 'Chặng 3' };
const IDX = { SCAN: 0, SKELETON: 1, OVERVIEW: 2, LOCALIZE: 3, SPLIT: 4, PLAN: 5, WRITE: 6, HANDOFF: 7 } as const;
const STEP_TO_IDX: Record<string, number> = { SCAN: IDX.SCAN, SKELETON: IDX.SKELETON, OVERVIEW: IDX.OVERVIEW, LOCALIZE: IDX.LOCALIZE, ALIAS_LOCALIZE: IDX.LOCALIZE, SPLIT: IDX.SPLIT, WRITE: IDX.WRITE, SEASON_READER: IDX.WRITE };   // 3e (R3): người đọc cả mùa là phần cuối của «Viết» trên dải — KHÔNG thêm ô mới (L13)

export interface NsItem { key: NsItemKey; label: string; state: NsItemState; detail?: string }
export interface NsStage { key: NsStageKey; title: string; items: NsItem[]; state: NsStageState }

/** Nhãn đang chạy ở dải (D-FE: OVERVIEW "Đang tạo tổng quan", LOCALIZE "Đang bản địa hoá"). */
export const NS_STEP_LABEL: Record<string, string> = {
  SCAN: 'Đang quét truyện', SKELETON: 'Đang dựng khung xương', OVERVIEW: 'Đang tạo tổng quan', LOCALIZE: 'Đang bản địa hoá', ALIAS_LOCALIZE: 'Đang bản địa hoá',
  SPLIT: 'Đang chia tập', WRITE: 'Đang viết kịch bản', SEASON_READER: 'Đang đọc cả mùa',
};
export const nsStepLabel = (step: string | null | undefined): string => (step && NS_STEP_LABEL[step]) || 'Đang chạy';

/** Mã lý do máy KHÔNG tự chốt dàn tập → chữ người đọc được (chép từ BE `PLAN_STOP_REASON_TEXT`). Mã lạ ⇒ câu chung, KHÔNG lộ mã thô. */
export const PLAN_STOP_REASON_TEXT: Record<string, string> = {
  PLAN_STALE: 'Dàn tập đã lỗi thời — chia lại hoặc chốt lại',
  PLAN_APPROVED: 'Dàn tập đã được chốt',
  GATE_BLOCKED: 'Cổng AI chấm đang chặn — xử lý rồi chốt',
  PLAN_ERRORS: 'Dàn tập còn lỗi mức ERROR — sửa rồi chốt',
  JOB_RUNNING: 'Truyện đang có việc khác chạy',
  AUTO_APPROVE_FAILED: 'Máy chưa chốt được dàn tập (khung xương vừa đổi) — xem lại rồi chốt',
  // 3d1: cổng ④ (chia tập) còn lỗi sau khi máy tự sửa / chưa chấm xong / nội dung đã đổi sau lần chấm (chép từ BE `PLAN_STOP_REASON_TEXT`).
  REVIEW_GATE_UNRESOLVED: 'Cổng AI chấm dàn tập còn lỗi sau khi máy tự sửa — xem lỗi ở ④ rồi chốt',
  GATE_MISSING: 'Cổng AI chấm dàn tập chưa chấm xong hoặc bị lỗi — chấm lại hoặc xem tay rồi chốt',
  GATE_STALE: 'Dàn tập đã đổi sau lần chấm cuối — chấm lại hoặc xem tay rồi chốt',
};
const PLAN_STOP_FALLBACK = 'Máy chưa tự chốt được dàn tập — xem lại dàn tập rồi chốt';
export const planStopReasonLines = (codes: ReadonlyArray<string> | null | undefined): string[] => {
  const list = (codes ?? []).map((c) => PLAN_STOP_REASON_TEXT[c] ?? PLAN_STOP_FALLBACK);
  const uniq = list.filter((x, i) => list.indexOf(x) === i);
  return uniq.length ? uniq : [PLAN_STOP_FALLBACK];
};

export type CursorMode = 'run' | 'stopped' | 'error' | 'wait' | 'blocked' | 'gate' | 'budget' | 'review';
export interface Cursor { idx: number; mode: CursorMode }

/** Con trỏ "đang đứng ở đâu" của chuỗi; null = chưa có chuỗi hoặc đã huỷ. */
const cursorOf = (v: NsView): Cursor | null => {
  const run = v.appendRun ?? null;
  if (run) {
    if (!isNewStoryRun(run) || run.status === 'CANCELLED') return null;
    if (run.status === 'DONE') return { idx: IDX.HANDOFF, mode: 'wait' };
    if (run.status === 'WAIT_SKELETON') return { idx: IDX.LOCALIZE, mode: 'gate' };
    if (run.status === 'WAIT_EPISODE_PLAN') return { idx: IDX.PLAN, mode: 'wait' };
    if (run.status === 'WAIT_REVIEW_GATE') return { idx: IDX.LOCALIZE, mode: 'review' };   // 3d1: chờ người quyết lỗi cổng ③, bước giữ ở Bản địa hoá
    if (run.status === 'WAIT_BUDGET') return { idx: run.step ? (STEP_TO_IDX[run.step] ?? IDX.SKELETON) : IDX.SKELETON, mode: 'budget' };   // E3-P3c2a: chờ người bấm Chạy tiếp, bước giữ nguyên
    const idx = run.step ? (STEP_TO_IDX[run.step] ?? IDX.SKELETON) : IDX.SKELETON;
    return { idx, mode: run.error ? 'error' : v.interrupted ? 'stopped' : 'run' };
  }
  const pre = v.newStoryRun;
  if (!pre) return null;
  return { idx: pre.step === 'SCAN' ? IDX.SCAN : IDX.SKELETON, mode: pre.state === 'RUNNING' ? 'run' : pre.state === 'INTERRUPTED' ? 'stopped' : pre.state === 'WAIT_BUDGET' ? 'budget' : pre.state === 'WAIT_REVIEW_GATE' ? 'review' : 'blocked' };
};

/** 3h1 — con trỏ chuỗi cho cột bước (`stageNavView`). */
export const nsCursor = (v: NsView): Cursor | null => cursorOf(v);
/** Bước giao diện ①–⑤ mà con trỏ đang đứng. `gate` (dừng NGAY SAU Chặng 1, STOP 1) tính là ② vì đó là nơi người duyệt. */
export const nsCursorStage = (c: Cursor): WorkbenchStageKey => (c.mode === 'gate' ? 'skeleton' : ITEMS[c.idx].wb);

const itemState = (i: number, c: Cursor | null): NsItemState => {
  if (!c) return 'todo';
  if (i < c.idx) return 'done';
  if (i > c.idx) return 'todo';
  switch (c.mode) {
    case 'run': return 'run';
    case 'stopped': return 'stopped';
    case 'error': return 'error';
    case 'wait': case 'blocked': case 'budget': case 'review': return 'wait';
    default: return 'todo';   // 'gate': dừng NGAY SAU bước trước, bước này chưa bắt đầu
  }
};

const stageStateOf = (items: NsItem[], gateHere: boolean): NsStageState => {
  const has = (s: NsItemState) => items.some((x) => x.state === s);
  if (has('error')) return 'error';
  if (has('stopped')) return 'stopped';
  if (has('run')) return 'run';
  if (has('wait') || gateHere) return 'wait';
  return items.every((x) => x.state === 'done') ? 'done' : 'todo';
};

export const nsStages = (v: NsView): NsStage[] => {
  const c = cursorOf(v);
  const run = v.appendRun ?? null;
  const machineApproved = !!run?.autoApprovedPlan;
  const items: NsItem[] = ITEMS.map((d, i) => {
    const state = itemState(i, c);
    const detail = d.key === 'PLAN' && state === 'done' && machineApproved ? 'Máy tự chốt (sạch)' : undefined;
    return { key: d.key, label: d.label, state, ...(detail ? { detail } : {}) };
  });
  const gateStage: NsStageKey | null = c?.mode === 'gate' ? 'c1' : null;
  return (['c1', 'c2', 'c3'] as NsStageKey[]).map((k) => {
    const its = items.filter((_, i) => ITEMS[i].stage === k);
    return { key: k, title: STAGE_TITLE[k], items: its, state: stageStateOf(its, gateStage === k) };
  });
};

export const NS_ICON: Record<NsItemState, string> = { done: '✓', run: '⏳', wait: '⏸', stopped: '⏸', todo: '○', error: '⚠' };

// ─── Dải: loại, nút, lý do ──────────────────────────────────────────────────

export type NsKind = 'none' | 'start' | 'start_blocked' | 'running' | 'interrupted' | 'blocked' | 'wait_skeleton' | 'wait_plan' | 'wait_budget' | 'wait_review_gate' | 'done' | 'cancelled';
export type NsActionId = 'start' | 'resumeBeforeSave' | 'resumeAfterSave' | 'approveSkeleton' | 'openSkeleton' | 'approvePlan' | 'openSplit' | 'openOverview' | 'openHandoff' | 'cancel' | 'continueBudget' | 'openLocalize' | 'regradeGate' | 'continueReviewGate' | 'acceptVolumeIssues' | 'openSource' | 'pushBatch' | 'cancelBatch' | 'openBatchResults' | 'openReview' | 'recheck' | 'autoRepairStart' | 'autoRepairStop';
export interface NsAction {
  id: NsActionId; label: string; disabled?: boolean; disabledReason?: string; target?: WorkbenchTarget;
  /** E3-P3c2b — `approveSkeleton` của chuỗi truyện mới: SỐ THÔ «tối đa Chặng 2» đang hiện trên nhãn nút, gửi làm `expectedMaxUsd` (vắng ⇒ nút khoá). */
  expectedMaxUsd?: number;
}
/** 3h1 (A.7) — «Chờ bạn duyệt · Chặng 2/3»: nhãn, ba chấm chặng và sắc dải (`stop` = viền cam chờ người; `run` = máy chạy; `done` = xong; `idle` = không nổi). */
export interface NsPhase { label: string; dots: Array<'done' | 'now' | 'todo'>; tone: 'stop' | 'run' | 'done' | 'idle' }

export interface NewStoryBar {
  kind: NsKind;
  /** 3h1 — nhãn + chấm chặng ở đầu dải. */
  phase: NsPhase;
  /** 3h1 — các dòng «Máy bỏ qua N lỗi…» (cũng nằm trong `lines`); dải vẽ thêm link mở Sổ duyệt. */
  skippedLines: string[];
  title: string;
  stages: NsStage[];
  /** Đúng MỘT nút chính khi cần người ra tay (luôn nằm ở dải, dù đang mở chặng nào); null khi máy đang chạy (không có gì để bấm). */
  primary: NsAction | null;
  secondary: NsAction[];
  /** Dòng chữ dưới dải: lý do dừng / gợi ý. */
  lines: string[];
  /** Có ⇒ dải đóng được (chỉ DONE / CANCELLED) và nhớ theo id này. */
  dismissId?: string;
  /** E3-P3c2a — dòng xám khi có lời gọi AI bị đứt giữa chừng (null: không có). */
  callsLine: string | null;
}
type BarBody = Omit<NewStoryBar, 'callsLine' | 'phase' | 'skippedLines'>;
/** `approveBlock`: lý do khoá nút "Duyệt & chạy tiếp" ở dải (cùng luật nút của ② — xem `nsApproveBlock`); null/bỏ trống = không khoá. */
export interface NsCtx {
  stage: WorkbenchStageKey; dismissedId: string | null; approveBlock?: string | null;
  /** 261025 E3-P3f — nút «Duyệt & đẩy N tập» của Chặng 3 (từ `pushStripOf`). Vắng / `legacy` ⇒ dải giữ nút «Đẩy sang Chapters» cũ (mở hộp đẩy hiện có). */
  push?: PushStrip | null;
  /** 261025 E3-P3f — lô đẩy mới nhất (để hiện «Xem kết quả» / «Dừng đẩy»). */
  batch?: Pick<WorkbenchHandoffBatchView, 'status' | 'cancelRequested'> | null;
  /** 3h1 — số việc «cần bạn» của Sổ duyệt (`reviewEntryView.needCount`) để ghi lên nút «Mở sổ duyệt». */
  reviewNeed?: number;
  /** 261023 E3-P3j0 (P2) — tập «Cần kiểm lại» (từ `recheckStripOf`). Có ⇒ nút đỏ DUY NHẤT của dải DONE là «Kiểm lại»; «Duyệt & đẩy N tập» (nếu còn tập đạt) hạ thành nút phụ. Vắng ⇒ dải y như trước. */
  recheck?: RecheckStrip | null;
  /** 261023 E3-P3j2b-2 (D8) — «Để máy sửa N tập» (từ `autoRepairStripOf`). `running` ⇒ KHÔNG nút đỏ, dải «Máy đang sửa tập n…» + «Dừng»; `ready` ⇒ nút đỏ DUY NHẤT; «Kiểm lại» / «Duyệt & đẩy» hạ thành nút phụ. Vắng (cờ tắt) ⇒ dải y như trước. */
  autoRepair?: AutoRepairStrip | null;
}

/** 3h1 (H2) — câu ở chỗ dừng cuối Chặng 2: dừng ĐÚNG ở nút «Duyệt & đẩy», rồi máy đẩy (thay câu «Chặng 3 không tự chạy…» cũ). */
export const DONE_WAIT_LINE = 'Chờ bạn xem lướt kịch bản — bấm “Duyệt & đẩy”, máy sẽ đẩy sang Chapters.';
export const DONE_WAIT_LINE_LEGACY = 'Chờ bạn xem lướt kịch bản — bấm “Đẩy sang Chapters” để mở hộp đẩy.';
const openReviewAction = (n: number | undefined): NsAction => ({ id: 'openReview', label: n && n > 0 ? `${n} việc còn cần bạn · Mở sổ duyệt` : 'Mở sổ duyệt' });

/** Nút "Đẩy sang Chapters…" của ⑤ có là nút chính không: chuỗi truyện mới đã XONG và dải còn hiện (chưa đóng) ⇒ dải giữ nút chính "Đẩy sang Chapters", nút của ⑤ thành nút phụ. */
export const nsHandoffIsPrimary = (run: Pick<AppendRun, 'kind' | 'status' | 'id'> | null | undefined, dismissedId: string | null | undefined): boolean =>
  !(run && isNewStoryRun(run) && run.status === 'DONE' && run.id !== dismissedId);

/**
 * Lý do khoá nút "Duyệt & chạy tiếp" ở dải = ĐÚNG lý do khoá nút cùng tên ở ② (lỗi khung xương trước, rồi lý do tổng quan), tính từ `data.record`
 * đang có ở trang — không gọi thêm route. Chưa có bản ghi / chưa có khung xương ⇒ có lý do (không bấm được khi chưa thấy gì để duyệt).
 */
export const nsApproveBlock = (record: OutlineRecord | null | undefined, chapterUnitMax?: number | null): string | null => {
  const outline = record?.outline ?? null;
  const violations = dedupeViolations(withoutEpisodeClaimCodes(record?.violations ?? []));
  const base = approveBlock(canApprove(outline, violations), approveView(record));
  if (base) return base;   // lỗi khung xương / tổng quan đứng trước (một lý do mỗi lần)
  // 3g (Q5): số tập ĐÃ LƯU vượt số chương ⇒ BE sẽ 409 `EPISODES_BEYOND_CHAPTER_UNITS` — khoá nút ngay, chữ như BE.
  const saved = Number(outline?.params?.episodes);
  return typeof chapterUnitMax === 'number' && Number.isFinite(saved) && saved > chapterUnitMax ? episodesBeyondText(chapterUnitMax) : null;
};

/** Chữ lỗi số tập vượt số chương — CHÉP từ BE `episodesBeyondChapterUnitsText` (`utils/episodeSplitGate.ts`). */
export const episodesBeyondText = (max: number): string => `Số tập nhiều hơn số chương có thể chia (tối đa ${max}). Giảm số tập ở Khung xương rồi Duyệt & chạy tiếp.`;
/** Gợi ý dưới ô «Số tập» của hộp Bắt đầu; không có trần ⇒ null. */
export const startEpisodesHint = (max: number | null | undefined): string | null => (typeof max === 'number' ? `Tối đa ${max} tập (mỗi tập ít nhất một chương)` : null);

/** Nút duyệt của chính chặng (② ở chỗ dừng 1, ④ ở chỗ dừng 2) khi dải đã giữ nút chính: hạ thành nút phụ + một dòng chỉ lên dải. Chuỗi khác ⇒ giữ nguyên nút chính. */
export const nsStageApproveButton = (run: Pick<AppendRun, 'kind' | 'status'> | null | undefined): { primary: boolean; hint: string | null } => {
  if (run && isNewStoryRun(run) && (run.status === 'WAIT_SKELETON' || run.status === 'WAIT_EPISODE_PLAN')) {
    return { primary: false, hint: run.status === 'WAIT_SKELETON' ? 'Duyệt bằng nút đỏ ở dải phía trên' : 'Chốt bằng nút đỏ ở dải phía trên' };
  }
  return { primary: true, hint: null };
};

/** E3-P3c2b (H13) — mảnh chữ có/không đậm (để vẽ «**Duyệt & chạy tiếp**» mà không cần HTML trong chuỗi). */
export interface NsTextPart { text: string; bold?: boolean }
export const NS_DRAFT_HINT: NsTextPart[] = [
  { text: 'Đọc lại khung xương và tổng quan rồi bấm ' }, { text: 'Duyệt & chạy tiếp', bold: true }, { text: ' ở trên — một lần bấm chốt cả hai' },
];
export const nsTextOf = (parts: ReadonlyArray<NsTextPart>): string => parts.map((p) => p.text).join('');

export interface NsStopOneControls {
  /** Chuỗi truyện mới đang ở chỗ dừng 1: MỘT nút chính duy nhất, ở dải. */
  chainStop1: boolean;
  /** Nút «(Chốt tổng quan &) chạy tiếp» ở thẻ bản nháp của ②. Chuỗi truyện mới ⇒ ẩn (nút này đi cùng đường `continueAppendRun` nhưng KHÔNG mang `expectedMaxUsd` ⇒ BE 400). */
  cardContinue: { show: boolean; primary: boolean; hint: string | null };
  /** Nút «Chốt tổng quan» / «Duyệt khung xương» ở thẻ Tổng quan truyện (chỉ phía chưa chốt; nút «Mở để sửa» không đổi). Chuỗi truyện mới ⇒ ẩn. */
  overviewApprove: { show: boolean };
  /** Câu thay cho «Không còn lỗi nào — … bấm Chốt tổng quan…» ở thẻ bản nháp; null ⇒ giữ câu cũ. */
  draftHint: NsTextPart[] | null;
}
/** Quyết định MỘT nơi: chuỗi `NEW_STORY` ở `WAIT_SKELETON` ⇒ chỉ giữ nút ở dải; mọi trường hợp khác (không chuỗi, chuỗi APPEND, trạng thái khác) giữ NGUYÊN như trước. */
export const nsStopOneControls = (run: Pick<AppendRun, 'kind' | 'status'> | null | undefined): NsStopOneControls => {
  if (run && isNewStoryRun(run) && run.status === 'WAIT_SKELETON') {
    return { chainStop1: true, cardContinue: { show: false, primary: false, hint: null }, overviewApprove: { show: false }, draftHint: NS_DRAFT_HINT };
  }
  const b = nsStageApproveButton(run);
  return { chainStop1: false, cardContinue: { show: true, primary: b.primary, hint: b.hint }, overviewApprove: { show: true }, draftHint: null };
};

export const NS_NEED_POINTS_UP_TEXT = 'Khung xương và tổng quan chờ bạn duyệt — bấm “Duyệt & chạy tiếp” ở dải phía trên (một lần bấm chốt cả hai)';
/**
 * Mục «Cần bạn xem» ở chỗ dừng 1 của chuỗi truyện mới: `SKELETON_NOT_APPROVED` («cần chốt tổng quan trước khi làm bước sau») không đòi chốt riêng mà trỏ lên dải (không nút «Đi tới»).
 * Mã khác (vd `OVERVIEW_NEEDS_FIX` — việc phải sửa thật) và mọi trường hợp ngoài chỗ dừng 1 ⇒ null (vẽ như cũ).
 */
export const nsNeedReasonView = (run: Pick<AppendRun, 'kind' | 'status'> | null | undefined, reason: { code: string }): { text: string; goTo: false } | null =>
  (nsStopOneControls(run).chainStop1 && reason.code === 'SKELETON_NOT_APPROVED' ? { text: NS_NEED_POINTS_UP_TEXT, goTo: false } : null);

export const START_BLOCK_TEXT: Record<string, string> = {
  LOCALIZE_DISABLED: 'Truyện này tắt bản địa hoá — chưa chạy tự động được, làm tay từng bước ở ①–⑤.',
  NO_CHAPTERS: 'Chưa có chương nguồn — nạp truyện ở ① trước.',
  JOB_RUNNING: 'Truyện đang có việc khác chạy — chờ xong rồi bấm Bắt đầu.',
  ESTIMATE_UNKNOWN: 'Máy chưa kiểm tra được điều kiện chạy — chưa bắt đầu tự động được, báo kỹ thuật.',
};

/** 3e (R6/L13) — dòng người đọc ở chỗ dừng cuối Chặng 2: BE dựng sẵn (`readerLine`), FE chỉ in. Vắng / rỗng (BE cũ) ⇒ không dòng. */
export const nsReaderLine = (v: Pick<NsView, 'readerLine'> | null | undefined): string | null => {
  const t = typeof v?.readerLine === 'string' ? v.readerLine.trim() : '';
  return t || null;
};
/** 3e (L13) — «Máy tự sửa N tập ở bước viết»: số tập có dấu vết máy sửa (`autoFix`, mọi trạng thái — cả tập người đã hoàn tác vẫn là máy đã sửa). 0 ⇒ không dòng. */
export const nsAutoFixLine = (v: Pick<NsView, 'episodes'> | null | undefined): string | null => {
  const n = (v?.episodes ?? []).filter((e) => !!e.autoFix).length;
  return n > 0 ? `Máy tự sửa ${n} tập ở bước viết` : null;
};

const writtenOf = (v: NsView): { written: number; listed: number } => {
  const listed = v.outlineFlags?.episodesListed ?? v.episodes?.length ?? 0;
  const written = v.episodes ? v.episodes.filter((e) => e.written === 'draft').length : listed;
  return { written, listed };
};

/** E3-P3c2b — ngữ cảnh của dải «Chờ bạn quyết»: bước hiện hành của chuỗi + số cần để chạy nốt bước (H9). DÙNG CHUNG với nút «Chạy tiếp» để hai nơi cùng một ngữ cảnh. */
export const waitBudgetCtxOf = (v: Pick<NsView, 'appendRun' | 'waitBudgetStepEstimate' | 'continuation'> | null | undefined): { runStep: string | null; stepEstimate: NonNullable<NsView['waitBudgetStepEstimate']> | null; continuation: NonNullable<NsView['continuation']> | null } => ({
  runStep: v?.appendRun?.step ?? null, stepEstimate: v?.waitBudgetStepEstimate ?? null, continuation: v?.continuation ?? null,   // 3h5b: BE cũ ⇒ continuation null ⇒ cầu nối 3h5a
});

/** E3-P3c2a/3h5a — dải «Chờ bạn quyết · Chặng n/3» (n theo bước bị chặn): đúng MỘT nút chính «Chạy tiếp» (cầu nối B11: gọi route nâng-và-chạy-tiếp một lần / chỉ chạy lại / khoá kèm câu không số). */
const waitBudgetBar = (v: NsView, stages: NsStage[], resumeId: 'resumeBeforeSave' | 'resumeAfterSave', secondary: NsAction[]): BarBody => {
  const w = waitBudgetView(budgetOf(v), waitBudgetCtxOf(v));
  const primary: NsAction = w.mode === 'resume'
    ? { id: resumeId, label: 'Chạy tiếp' }
    : { id: 'continueBudget', label: CONTINUE_LABEL, ...(w.canContinue ? {} : { disabled: true, disabledReason: w.disabledReason ?? 'Chưa chạy tiếp được' }) };
  return { kind: 'wait_budget', title: `Truyện mới · ${w.label}`, stages, primary, secondary, lines: [w.sentence, ...(w.note ? [w.note] : [])] };
};

/** 261025 E3-P3f: ô «Đẩy sang Chapters» của Chặng 3 theo lô đẩy: đang đẩy ⇒ `run`; đã đẩy hết ⇒ `done`; còn lại giữ `wait` (chặng 3 không tự chạy). */
const withHandoffStage = (stages: NsStage[], mode: PushStrip['mode']): NsStage[] => {
  const st: NsItemState | null = mode === 'running' ? 'run' : mode === 'allPushed' ? 'done' : null;
  if (!st) return stages;
  return stages.map((g) => (g.key !== 'c3' ? g : {
    ...g, state: st,
    items: g.items.map((i) => (i.key === 'HANDOFF' ? { ...i, state: st } : i)),
  }));
};

/** 3h2/C2 (mockup `#sc=C&ph=0`): nút và câu đậm ở dải chưa bắt đầu — không số tiền. */
export const START_LABEL = 'Bắt đầu — máy tự chạy Chặng 1';
export const startTitle = (chapters: number | null | undefined): string =>
  `Truyện đã nạp ${typeof chapters === 'number' && chapters > 0 ? `${chapters} chương` : 'chương'}, máy chưa chạy gì. Bấm “Bắt đầu”: máy tự quét nguồn rồi dựng khung xương và tổng quan, sau đó dừng chờ bạn duyệt.`;

const barBody = (v: NsView | null | undefined, ctx: NsCtx): BarBody => {
  const none: BarBody = { kind: 'none', title: '', stages: [], primary: null, secondary: [], lines: [] };
  if (!v) return none;
  const run = v.appendRun ?? null;
  const stages = nsStages(v);
  const cancel = (running: boolean): NsAction => ({ id: 'cancel', label: 'Dừng chuỗi', disabled: running, ...(running ? { disabledReason: 'Đang chạy — chờ bước này xong (hoặc tới điểm dừng) rồi dừng' } : {}) });

  if (run) {
    if (!isNewStoryRun(run)) return none;
    if (run.status === 'CANCELLED') {
      return run.id === ctx.dismissedId ? none : { kind: 'cancelled', title: 'Truyện mới · đã dừng chuỗi', stages: [], primary: null, secondary: [], lines: ['Những gì đã làm được giữ nguyên.'], dismissId: run.id };
    }
    if (run.status === 'DONE') {
      if (run.id === ctx.dismissedId) return none;
      const { written, listed } = writtenOf(v);
      const count = written < listed ? `Đã viết ${written}/${listed} tập` : `Đã viết ${written} tập`;
      const head = [nsReaderLine(v), nsAutoFixLine(v)].filter((x): x is string => !!x);
      const push = ctx.push && ctx.push.mode !== 'legacy' ? ctx.push : null;
      // 261023 E3-P3j2b-2 (D8): ưu tiên nút chính — máy đang sửa (không nút đỏ, có «Dừng») › «Để máy sửa N tập» (nút đỏ) › «Kiểm lại» › «Duyệt & đẩy». Cờ tắt ⇒ `ctx.autoRepair` vắng ⇒ rơi xuống luật cũ.
      const ar = ctx.autoRepair ?? null;
      if (ar) {
        const batch = ctx.batch ?? null;
        const rcAll = push && push.mode !== 'running' ? ctx.recheck ?? null : null;
        const recheckSec: NsAction[] = rcAll ? [{ id: 'recheck', label: RECHECK_LABEL, ...(rcAll.disabled ? { disabled: true, disabledReason: rcAll.disabledReason ?? undefined } : {}) }] : [];
        const pushSec: NsAction[] = push && push.mode === 'ready' ? [{ id: 'pushBatch', label: push.label, ...(push.disabled ? { disabled: true, disabledReason: push.disabledReason ?? undefined } : {}) }] : [];
        const results: NsAction[] = batch ? [{ id: 'openBatchResults', label: 'Xem kết quả lần đẩy' }] : [];
        if (ar.mode === 'running') {
          return {
            kind: 'done', title: `Truyện mới · ${count} · máy đang sửa`, stages, primary: null,
            secondary: [{ id: 'autoRepairStop', label: AUTO_REPAIR_STOP_LABEL }, openReviewAction(ctx.reviewNeed)],
            lines: [ar.sentence, ...head],
          };
        }
        return {
          kind: 'done', title: `Truyện mới · ${count} · Chặng 3: đẩy sang Chapters`, stages: push ? withHandoffStage(stages, push.mode) : stages,
          primary: { id: 'autoRepairStart', label: ar.label },
          secondary: [...recheckSec, ...pushSec, openReviewAction(ctx.reviewNeed), ...results],
          lines: [ar.sentence, ...head, ...(push?.line && push.mode !== 'running' ? [push.line] : [])], dismissId: run.id,
        };
      }
      // 261023 E3-P3j0 (P2): có tập «Cần kiểm lại» (và không đang đẩy) ⇒ MỘT nút đỏ = «Kiểm lại»; «Duyệt & đẩy N tập» chỉ còn là nút phụ khi có tập đạt để đẩy. Một câu nói vì sao. Không tiền.
      const rc = push && push.mode !== 'running' ? ctx.recheck ?? null : null;
      if (push && rc) {
        const batch = ctx.batch ?? null;
        const pushSecondary: NsAction[] = push.mode === 'ready' ? [{ id: 'pushBatch', label: push.label, ...(push.disabled ? { disabled: true, disabledReason: push.disabledReason ?? undefined } : {}) }] : [];
        return {
          kind: 'done', title: `Truyện mới · ${count} · Chặng 3: đẩy sang Chapters`, stages: withHandoffStage(stages, push.mode),
          primary: { id: 'recheck', label: RECHECK_LABEL, ...(rc.disabled ? { disabled: true, disabledReason: rc.disabledReason ?? undefined } : {}) },
          secondary: [...pushSecondary, openReviewAction(ctx.reviewNeed), ...(batch ? [{ id: 'openBatchResults', label: 'Xem kết quả lần đẩy' } as NsAction] : [])],
          lines: [rc.sentence, ...head, ...(push.line ? [push.line] : [])], dismissId: run.id,
        };
      }
      if (push) {
        // 261025 E3-P3f: Chặng 3 = «Duyệt & đẩy N tập» (đẩy THEO DANH SÁCH TẬP, việc nền). Vẫn không tự chạy — người bấm.
        const batch = ctx.batch ?? null;
        const secondary: NsAction[] = [
          openReviewAction(ctx.reviewNeed),
          ...(push.mode === 'running'
            ? [{ id: 'cancelBatch', label: 'Dừng đẩy', ...(batch?.cancelRequested ? { disabled: true, disabledReason: 'Đã gửi lệnh dừng — máy dừng trước tập kế' } : {}) } as NsAction]
            : batch ? [{ id: 'openBatchResults', label: 'Xem kết quả lần đẩy' } as NsAction] : []),
        ];
        return {
          kind: 'done', title: `Truyện mới · ${count} · Chặng 3: đẩy sang Chapters${push.mode === 'running' ? ` — ${push.label}` : ''}`, stages: withHandoffStage(stages, push.mode),
          primary: { id: 'pushBatch', label: push.label, ...(push.disabled ? { disabled: true, disabledReason: push.disabledReason ?? undefined } : {}) },
          secondary,
          lines: [...head, ...(push.line && push.mode !== 'running' ? [push.line] : []), ...(push.mode === 'allPushed' || push.mode === 'partial' || push.mode === 'running' ? [] : [DONE_WAIT_LINE])], dismissId: run.id,
        };
      }
      return {
        kind: 'done', title: `Truyện mới · ${count} · Chặng 3: đẩy sang Chapters`, stages,
        primary: { id: 'openHandoff', label: 'Đẩy sang Chapters', target: { stage: 'script', intent: 'handoff' } },
        secondary: [openReviewAction(ctx.reviewNeed)], lines: [...head, DONE_WAIT_LINE_LEGACY], dismissId: run.id,
      };
    }
    if (run.status === 'WAIT_SKELETON') {
      const here = ctx.stage === 'skeleton';
      const block = ctx.approveBlock ?? null;
      // E3-P3c2b/3h5a: nút KHÔNG hiện số nhưng vẫn gửi đúng `expectedMaxUsd` thô; chưa có số (BE cũ / unknown) ⇒ khoá kèm lý do không số. Lý do khung xương/tổng quan thắng lý do này.
      const s1 = stop1View(v.stop1Estimate);
      const disabledReason = block ?? s1.button.reason;
      return {
        kind: 'wait_skeleton', title: 'Truyện mới · Chặng 1 xong — chờ bạn duyệt khung xương', stages,
        primary: {
          id: 'approveSkeleton', label: s1.button.label,
          ...(disabledReason ? { disabled: true, disabledReason } : {}),
          ...(s1.button.expectedMaxUsd !== null ? { expectedMaxUsd: s1.button.expectedMaxUsd } : {}),
        },
        secondary: [...(here ? [] : [{ id: 'openSkeleton', label: 'Xem khung xương', target: { stage: 'skeleton' } } as NsAction]), cancel(false)],
        lines: [...nsSkeletonGateLines(v.skeletonGate), disabledReason ?? (here ? 'Xem khung xương và tổng quan ở dưới rồi bấm “Duyệt & chạy tiếp” — máy sẽ tự bản địa hoá, chia tập và viết.' : 'Duyệt xong, máy tự bản địa hoá, chia tập và viết.')],
      };
    }
    if (run.status === 'WAIT_EPISODE_PLAN') {
      const here = ctx.stage === 'split';
      return {
        kind: 'wait_plan', title: 'Truyện mới · máy chưa tự chốt dàn tập — chờ bạn', stages,
        primary: { id: 'approvePlan', label: 'Chốt dàn tập & chạy tiếp' },
        secondary: [...(here ? [] : [{ id: 'openSplit', label: 'Mở dàn tập để xem', target: { stage: 'split' } } as NsAction]), cancel(false)],
        lines: [...planStopReasonLines(run.planStopReasons), ...(here ? ['Xem dàn tập bên dưới rồi bấm “Chốt dàn tập & chạy tiếp” ở dải này.'] : [])],
      };
    }
    if (run.status === 'WAIT_BUDGET') return waitBudgetBar(v, stages, 'resumeAfterSave', [cancel(false)]);
    if (run.status === 'WAIT_REVIEW_GATE') {
      // 3d1 (D7/D8): máy đã sửa ≤ 2 vòng, cổng ③ vẫn còn lỗi. MỘT nút chính (đỏ) đưa người tới ③; hai nút phụ: «chấm lại sau khi sửa» (đường «Chạy tiếp» — nội dung chưa đổi thì BE giữ nguyên trạng thái chờ, 0 AI)
      // và «Chạy tiếp, để lỗi xem sau» (route D8, có hộp xác nhận một bước).
      const g = gateWaitView(run.gateWait);
      const here = ctx.stage === 'localize';
      return {
        kind: 'wait_review_gate', title: `Truyện mới · ${g.label}`, stages,
        // Đang đứng ở ③ thì «Xem lỗi» vô nghĩa ⇒ nút chính là «chấm lại»; ở chặng khác ⇒ nút chính đưa tới ③.
        primary: here ? { id: 'regradeGate', label: GATE_REGRADE_LABEL } : { id: 'openLocalize', label: g.openLabel, target: { stage: 'localize' } },
        secondary: [
          ...(here ? [] : [{ id: 'regradeGate', label: GATE_REGRADE_LABEL } as NsAction]),
          // 3g2 (Q9): ③ BLOCKED ⇒ không có nút «để lỗi xem sau» (BE 409 GATE_BLOCKED); người xử lý ở thẻ Bản địa hoá («Vẫn tiếp tục» kèm lý do).
          ...(g.blocked ? [] : [{ id: 'continueReviewGate', label: GATE_CONTINUE_LABEL } as NsAction]),
          cancel(false),
        ],
        lines: [
          g.sentence, g.reason, ...g.issueLines, ...(g.blocked ? [GATE_BLOCKED_LINE] : []),
          ...(here ? [g.blocked ? 'Xử lý ở thẻ Bản địa hoá bên dưới rồi bấm “Tôi đã sửa — chấm lại”.' : 'Sửa các lỗi ở dưới rồi bấm “Tôi đã sửa — chấm lại”, hoặc bấm “Chạy tiếp, để lỗi xem sau”.'] : []),
        ],
      };
    }
    // RUNNING
    if (run.error || v.interrupted) {
      const strip = interruptedStripView(run);
      return {
        kind: 'interrupted', title: `Truyện mới · dừng ở ${nsStepLabel(run.step).replace(/^Đang /, '')}`, stages,
        primary: { id: 'resumeAfterSave', label: 'Chạy tiếp' },
        secondary: [...(strip.goTo ? [{ id: 'openOverview', label: 'Mở Tổng quan truyện', target: strip.goTo } as NsAction] : []), cancel(false)],
        lines: [strip.text],
      };
    }
    return { kind: 'running', title: `Truyện mới · ${nsStepLabel(run.step)}`, stages, primary: null, secondary: [cancel(true)], lines: [] };
  }

  const pre = v.newStoryRun ?? null;
  if (pre) {
    if (pre.state === 'RUNNING') return { kind: 'running', title: `Truyện mới · ${nsStepLabel(pre.step)}`, stages, primary: null, secondary: [], lines: [] };
    if (pre.state === 'WAIT_BUDGET') return waitBudgetBar(v, stages, 'resumeBeforeSave', []);
    if (pre.state === 'WAIT_REVIEW_GATE') {
      // 3d3 (L5/L6): cổng AI ① «quét quyển» còn quyển chưa sạch — chuỗi dừng TRƯỚC khung xương. Hai nút: «Chạy tiếp» (nút chính, đỏ — sửa chương rồi chấm lại quyển đã đổi / chấm lại quyển lỗi máy)
      // và «Chạy tiếp, để lỗi xem sau» (`acceptVolumeIssues`, có hộp xác nhận). Đang ở ① thì không cần nút «mở Nguồn».
      const here = ctx.stage === 'source';
      return {
        kind: 'wait_review_gate', title: `Truyện mới · ${VOLUME_GATE_LABEL}`, stages,
        primary: { id: 'resumeBeforeSave', label: VOLUME_GATE_RESUME_LABEL },
        secondary: [
          ...(here ? [] : [{ id: 'openSource', label: VOLUME_GATE_OPEN_SOURCE_LABEL, target: { stage: 'source' } } as NsAction]),
          { id: 'acceptVolumeIssues', label: VOLUME_GATE_ACCEPT_LABEL },
        ],
        lines: volumeGateBarLines(pre.volumeGate),
      };
    }
    if (pre.state === 'BLOCKED_BY_JOB') {
      return {
        kind: 'blocked', title: 'Truyện mới · chuỗi đang chờ', stages,
        primary: { id: 'resumeBeforeSave', label: 'Chạy tiếp', disabled: true, disabledReason: 'Đang chạy việc khác của truyện — chờ xong' },
        secondary: [], lines: ['Đang chạy việc khác của truyện — chờ xong rồi bấm “Chạy tiếp”.'],
      };
    }
    return {
      kind: 'interrupted', title: `Truyện mới · dừng ở ${pre.step === 'SCAN' ? 'quét truyện' : 'dựng khung xương'}`, stages,
      primary: { id: 'resumeBeforeSave', label: 'Chạy tiếp' }, secondary: [],
      lines: ['Chuỗi bị gián đoạn trước khi lưu khung xương — bấm “Chạy tiếp” (việc đã xong không phải chạy lại).'],
    };
  }

  const can = v.canStartNewStory;
  if (!can) return none;
  if (can.available) {
    return {
      kind: 'start', title: startTitle(v.outlineFlags?.rawChapters), stages, primary: { id: 'start', label: START_LABEL }, secondary: [],
      lines: [],   // «Chạy nền, rời trang được» nằm trong hộp «Kiểm trước khi bấm»
    };
  }
  const text = can.reason ? START_BLOCK_TEXT[can.reason] : undefined;
  if (!text) return none;   // OUTLINE_EXISTS / CHAIN_IN_PROGRESS / mã lạ: không phải việc của dải này
  return {
    kind: 'start_blocked', title: 'Truyện mới', stages,
    primary: { id: 'start', label: 'Bắt đầu', disabled: true, disabledReason: text }, secondary: [], lines: [text],
  };
};

/** 3g2 (Q11/R7) — «Máy bỏ qua N lỗi ở <bước> — xem Sổ duyệt», mỗi bước có override của máy một dòng (thứ tự ① ③ ④). Không có ⇒ []. Thuần, từ trường BE `machineSkipped`. */
export const MACHINE_SKIPPED_STEP_TEXT: Record<'source' | 'localize' | 'split', string> = { source: 'Quét chương', localize: 'Bản địa hoá', split: 'Chia tập' };
export const machineSkippedLines = (v: Pick<NsView, 'machineSkipped'> | null | undefined): string[] =>
  (v?.machineSkipped ?? []).filter((m) => m && m.count > 0 && MACHINE_SKIPPED_STEP_TEXT[m.step]).map((m) => `Máy bỏ qua ${m.count} lỗi ở ${MACHINE_SKIPPED_STEP_TEXT[m.step]} — xem Sổ duyệt`);

const dotsOf = (n: 0 | 1 | 2 | 3, allDone = false): NsPhase['dots'] => ([1, 2, 3] as const).map((k) => (allDone || k < n ? 'done' : k === n ? 'now' : 'todo')) as NsPhase['dots'];
const changOf = (v: NsView): 1 | 2 => { const c = cursorOf(v); return c && c.mode !== 'gate' && c.idx >= IDX.LOCALIZE ? 2 : 1; };
const NS_PREFIX = /^Truyện mới · /;

/** 3h1 (A.7) — nhãn «Chờ bạn duyệt · Chặng n/3» + ba chấm + sắc dải, tính từ loại dải đã có (không đoán thêm). */
const phaseOfBar = (body: BarBody, v: NsView, ctx: NsCtx): NsPhase => {
  const mk = (label: string, n: 0 | 1 | 2 | 3, tone: NsPhase['tone'], allDone = false): NsPhase => ({ label, dots: dotsOf(n, allDone), tone });
  switch (body.kind) {
    case 'none': return mk('', 0, 'idle');
    case 'cancelled': return mk('Đã dừng', 0, 'idle');
    case 'start': case 'start_blocked': return mk('Chưa bắt đầu', 0, 'stop');
    case 'running': return mk(`Đang chạy · Chặng ${changOf(v)}/3`, changOf(v), 'run');
    case 'blocked': return mk(`Đang chờ · Chặng ${changOf(v)}/3`, changOf(v), 'run');
    case 'interrupted': return mk(`Chờ bạn quyết · Chặng ${changOf(v)}/3`, changOf(v), 'stop');
    case 'wait_skeleton': return mk('Chờ bạn duyệt · Chặng 1/3', 1, 'stop');
    case 'wait_plan': return mk('Chờ bạn duyệt · Chặng 2/3', 2, 'stop');
    case 'wait_budget': {
      const label = body.title.replace(NS_PREFIX, '');
      const n = /Chặng 2\/3/.test(label) ? 2 : 1;
      return mk(label, n, 'stop');
    }
    case 'wait_review_gate': { const n = v.appendRun ? 2 : 1; return mk(`Chờ bạn quyết · Chặng ${n}/3`, n, 'stop'); }
    case 'done': {
      const mode = ctx.push?.mode;
      if (body.secondary.some((a) => a.id === 'autoRepairStop')) return mk('Máy đang sửa', 3, 'run');
      if (mode === 'running') return mk('Đang chạy · Chặng 3/3', 3, 'run');
      if (mode === 'allPushed') return mk('Xong 3/3', 3, 'done', true);
      if (mode === 'partial') return mk('Chờ bạn xem · Chặng 3/3', 3, 'stop');
      return mk('Chờ bạn duyệt · Chặng 2/3', 2, 'stop');
    }
    default: return mk('', 0, 'idle');
  }
};

export const newStoryBar = (v: NsView | null | undefined, ctx: NsCtx): NewStoryBar => {
  const body = barBody(v, ctx);
  const hasChain = body.kind !== 'none' && body.kind !== 'start' && body.kind !== 'start_blocked' && body.kind !== 'cancelled';
  const b = hasChain ? budgetOf(v) : null;
  const skippedLines = hasChain ? machineSkippedLines(v) : [];
  return { ...body, ...(hasChain ? { lines: [...body.lines, ...skippedLines] } : {}), skippedLines, phase: phaseOfBar(body, v ?? {}, ctx), callsLine: interruptedCallsLine(b) };
};

/** Tiến độ bước từ job con đang chạy (`workbench.jobs.running`); không job / không tiến độ ⇒ ''. */
export const nsProgressText = (v: NsView | null | undefined, running: ReadonlyArray<WorkbenchRunningJob> | undefined): string => {
  if (!v) return '';
  const c = cursorOf(v);
  if (!c || c.mode !== 'run') return '';
  const kind: Record<number, WorkbenchRunningJob['kind']> = { 0: 'scan', 1: 'skeleton', 2: 'overview', 3: 'localize', 4: 'split', 6: 'write' };
  const j = (running ?? []).find((x) => x.kind === kind[c.idx]);
  return formatJobStepProgress(j?.progress);
};

/** Chữ xác nhận "Dừng chuỗi" sau khi lưu khung xương (không có gộp để hoàn tác). */
export const nsCancelCopy = (): { title: string; content: string; okText: string; danger: boolean } => ({
  title: 'Dừng chuỗi truyện mới?', content: 'Dừng chuỗi — khung xương, tổng quan, bản địa hoá, dàn tập và kịch bản đã có giữ nguyên.', okText: 'Dừng chuỗi', danger: true,
});

// ─── ② STOP 1 ──────────────────────────────────────────────────────────────

/** Chữ khung cảnh báo ở ② khi chuỗi truyện mới dừng chờ duyệt (không có "+n nhân vật" của phần mới). */
export const nsStopOneView = (run: Pick<AppendRun, 'kind'>, episodes: number): { message: string; episodesText: string; showCoreReview: boolean } | null => (
  isNewStoryRun(run)
    ? { message: 'Chặng 1 xong: khung xương và tổng quan đã dựng — duyệt rồi máy chạy tiếp chặng 2', episodesText: `Số tập: ${episodes} tập (AI đề xuất) — sửa ở ô Số tập trước khi duyệt.`, showCoreReview: false }
    : null
);

/** STOP 1 của chuỗi truyện mới ⇒ tự mở bước Khung xương MỘT lần cho mỗi đợt (không giật người dùng về lại khi họ đã đi chỗ khác). */
export const shouldAutoOpenStopOne = (run: Pick<AppendRun, 'kind' | 'status' | 'id'> | null | undefined, openedForId: string | null): boolean =>
  isNewStoryRun(run) && run?.status === 'WAIT_SKELETON' && run.id !== openedForId;

// ─── Dàn tập máy tự chốt (④) ───────────────────────────────────────────────

export const autoApprovedPlanLine = (run: Pick<AppendRun, 'kind' | 'autoApprovedPlan'> | null | undefined): string | null =>
  isNewStoryRun(run) && run?.autoApprovedPlan ? 'Máy tự chốt dàn tập (sạch)' : null;

// ─── Dấu chip chặng ─────────────────────────────────────────────────────────

const mk = (tone: StageMarkView['tone'], icon: string, text: string, tip: string): StageMarkView => ({ tone, icon, text, tip, attention: false });
const WB_ORDER: WorkbenchStageKey[] = ['source', 'skeleton', 'localize', 'split', 'script'];

/** Dấu chip theo chuỗi truyện mới; chưa có chuỗi / đã đóng ⇒ {} (không ghi đè). */
export const newStoryStageMarks = (v: NsView | null | undefined, ctx: { dismissedId: string | null }): Partial<Record<WorkbenchStageKey, StageMarkView>> => {
  if (!v) return {};
  const run = v.appendRun ?? null;
  const c = cursorOf(v);
  if (!c) return {};
  const out: Partial<Record<WorkbenchStageKey, StageMarkView>> = {};
  if (run?.status === 'DONE') {
    if (run.id === ctx.dismissedId) return {};
    for (const k of WB_ORDER) out[k] = mk('ok', '✓', '', 'Chuỗi truyện mới xong');
    return out;
  }
  const curWb: WorkbenchStageKey = c.mode === 'gate' ? 'skeleton' : ITEMS[c.idx].wb;
  const ci = WB_ORDER.indexOf(curWb);
  WB_ORDER.forEach((k, i) => {
    if (i < ci) out[k] = mk('ok', '✓', '', 'Xong');
    else if (i > ci) out[k] = mk('lock', '⏸', '', 'Chờ bước trước của chuỗi truyện mới');
    else if (c.mode === 'gate') out[k] = mk('warn', '●', 'duyệt', 'Chờ bạn duyệt khung xương');
    else if (c.mode === 'wait' && run?.status === 'WAIT_EPISODE_PLAN') out[k] = mk('warn', '●', 'chốt', 'Chờ bạn chốt dàn tập');
    else if (c.mode === 'budget') out[k] = mk('warn', '●', 'quyết', 'Chờ bạn bấm Chạy tiếp');
    else if (c.mode === 'review') out[k] = mk('warn', '●', 'lỗi', run ? 'Chờ bạn quyết lỗi cổng AI ở Bản địa hoá' : 'Chờ bạn quyết lỗi cổng AI ở bước quét quyển');
    else if (c.mode === 'stopped' || c.mode === 'error') out[k] = mk('warn', '●', 'dừng', friendlyJobNote(run?.error?.message) || 'Đã dừng — bấm "Chạy tiếp"');
    else if (c.mode === 'blocked') out[k] = mk('warn', '●', 'chờ', 'Đang chờ việc khác của truyện');
    else out[k] = mk('run', '⏳', '', 'Đang chạy chuỗi truyện mới');
  });
  return out;
};

// ─── Nút "Bắt đầu": form D6 ────────────────────────────────────────────────

export type NsAdaptMode = 'faithful' | 'core' | 'shell';
export type NsPolicyMode = 'WESTERNIZE' | 'KEEP' | 'MAP';
export interface StartForm { genre: string; episodes: string; minutesPerEpisode: string; adaptMode: NsAdaptMode; language: string; policyMode: NsPolicyMode; targetCulture: string }

export const defaultStartForm = (): StartForm => ({ genre: '', episodes: '', minutesPerEpisode: '2', adaptMode: 'core', language: 'English', policyMode: 'WESTERNIZE', targetCulture: '' });

export const ADAPT_MODE_LABEL: Record<NsAdaptMode, string> = { faithful: 'Sát nguyên tác', core: 'Giữ cốt lõi', shell: 'Chỉ giữ khung' };
export const POLICY_MODE_LABEL: Record<NsPolicyMode, string> = { WESTERNIZE: 'Tây hoá (đổi tên, bối cảnh)', KEEP: 'Giữ nguyên tên và bối cảnh', MAP: 'Chuyển sang một nền văn hoá' };

export const CULTURE_MIN = 2;
export const CULTURE_MAX = 60;

/** Lỗi từng ô (rỗng = hợp lệ). Cùng luật BE `normalizeNewStoryInputs`/`parseLocalizePolicy` để bắt lỗi trước khi gửi. */
export const startFormErrors = (f: StartForm, chapterUnitMax?: number | null): Partial<Record<keyof StartForm, string>> => {
  const e: Partial<Record<keyof StartForm, string>> = {};
  const ep = f.episodes.trim();
  if (ep !== '' && (!/^\d+$/.test(ep) || Number(ep) < 1)) e.episodes = 'Số tập phải là số nguyên từ 1 (hoặc để trống để AI đề xuất)';
  else if (ep !== '' && typeof chapterUnitMax === 'number' && Number(ep) > chapterUnitMax) e.episodes = `${startEpisodesHint(chapterUnitMax)} — hiện ${Number(ep)}`;   // 3g Q5: báo đỏ, KHÔNG tự kẹp
  const mins = Number(f.minutesPerEpisode);
  if (f.minutesPerEpisode.trim() === '' || !Number.isFinite(mins) || mins <= 0) e.minutesPerEpisode = 'Số phút mỗi tập phải lớn hơn 0';
  if (f.policyMode === 'MAP') {
    const t = f.targetCulture.trim();
    if (!t) e.targetCulture = 'Chọn nền văn hoá đích (vd "Persian")';
    else if (t.length < CULTURE_MIN || t.length > CULTURE_MAX) e.targetCulture = `Nền văn hoá dài ${CULTURE_MIN}–${CULTURE_MAX} ký tự`;
  }
  return e;
};

/** Body gửi BE (+ `expectedMaxUsd` nếu có). Ô trống ⇒ bỏ khoá (BE điền mặc định); `targetCulture` chỉ đi kèm MAP (BE từ chối với KEEP/WESTERNIZE). */
export const startBodyOf = (f: StartForm, expectedMaxUsd?: number | null): NewStoryRunStartBody => {
  const genre = f.genre.trim();
  const ep = f.episodes.trim();
  const lang = f.language.trim();
  return {
    ...(genre ? { genre } : {}),
    ...(ep ? { episodes: Number(ep) } : {}),
    minutesPerEpisode: Number(f.minutesPerEpisode),
    adaptMode: f.adaptMode,
    ...(lang ? { language: lang } : {}),
    localizePolicy: { mode: f.policyMode, ...(f.policyMode === 'MAP' ? { targetCulture: f.targetCulture.trim() } : {}) },
    // 3h5b: BE mới tự lấy ước làm trần (`SERVER_ESTIMATE`, bỏ qua `expectedMaxUsd`); BE cũ bỏ qua khoá này và dùng `expectedMaxUsd` ⇒ luôn gửi cả hai.
    budgetMode: 'SERVER_ESTIMATE',
    // E3-P3c2a: số «tối đa» người đã thấy ở khung (BE bắt buộc khi bắt đầu mới; lệch ⇒ 409 ESTIMATE_CHANGED).
    ...(typeof expectedMaxUsd === 'number' && Number.isFinite(expectedMaxUsd) ? { expectedMaxUsd } : {}),
  };
};

